import { z } from 'zod';
/**
 * The Notification Center (spec §13.6).
 *
 * **What is code and what is data (Rule 1).** Which events the system raises —
 * a reminder, a breach, a missed submission — is code: the event exists because
 * code emits it. What each message SAYS is data. Every template below carries
 * default wording so a fresh organisation is never silent, and an Org Admin
 * overrides that wording per channel from Master Data with no deploy. The
 * override lives in `notification_templates`; the default lives here.
 */
export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'push', 'sms'];
export const NOTIFICATION_TEMPLATES = [
    {
        key: 'escalation.reminder',
        app: null,
        label: 'Reminder before the deadline',
        description: 'Sent at each configured reminder point before an SLA elapses.',
        critical: true,
        mergeFields: ['entityLabel', 'dueAt', 'appLabel'],
        subjectEn: 'Reminder: {{entityLabel}} is due {{dueAt}}',
        bodyEn: '{{entityLabel}} in {{appLabel}} is waiting on you and is due {{dueAt}}.',
        subjectAr: 'تذكير: {{entityLabel}} مستحق في {{dueAt}}',
        bodyAr: '{{entityLabel}} في {{appLabel}} بانتظارك ومستحق في {{dueAt}}.',
    },
    {
        key: 'escalation.breach',
        app: null,
        label: 'Deadline missed',
        description: 'Sent once, when an SLA elapses with the item still open.',
        critical: true,
        mergeFields: ['entityLabel', 'dueAt', 'appLabel', 'priority'],
        subjectEn: 'Overdue: {{entityLabel}}',
        bodyEn: 'The deadline for {{entityLabel}} in {{appLabel}} passed on {{dueAt}}. It is now {{priority}}.',
        subjectAr: 'متأخر: {{entityLabel}}',
        bodyAr: 'انقضى الموعد النهائي لـ {{entityLabel}} في {{appLabel}} في {{dueAt}}. أولويته الآن {{priority}}.',
    },
    {
        key: 'escalation.raise',
        app: null,
        label: 'Escalated to the next level',
        description: 'Sent to the people at each escalation level, and repeated at its cadence.',
        critical: true,
        mergeFields: ['entityLabel', 'level', 'appLabel'],
        subjectEn: 'Escalated to {{level}}: {{entityLabel}}',
        bodyEn: '{{entityLabel}} in {{appLabel}} is still open and has escalated to {{level}}.',
        subjectAr: 'صُعّد إلى {{level}}: {{entityLabel}}',
        bodyAr: '{{entityLabel}} في {{appLabel}} لا يزال مفتوحًا وصُعّد إلى {{level}}.',
    },
    {
        key: 'qaqc.dg_log_missing',
        app: 'qaqc',
        label: 'Daily document governance log missing',
        description: 'Sent when a project has not submitted its daily log by the configured time.',
        critical: true,
        mergeFields: ['projectCode', 'projectName', 'deadlineTime', 'date'],
        subjectEn: 'Document governance log not submitted: {{projectCode}}',
        bodyEn: 'The daily document governance log for {{projectName}} was not submitted by {{deadlineTime}} on {{date}}.',
        subjectAr: 'لم يُقدَّم سجل حوكمة الوثائق: {{projectCode}}',
        bodyAr: 'لم يُقدَّم سجل حوكمة الوثائق اليومي لـ {{projectName}} بحلول {{deadlineTime}} في {{date}}.',
    },
    {
        key: 'distribution.digest',
        app: null,
        label: 'Scheduled report',
        description: 'Sent to a distribution list when its report runs, with the report attached.',
        critical: false,
        mergeFields: ['reportTitle', 'listName', 'period', 'appLabel'],
        subjectEn: '{{reportTitle}}: {{period}}',
        bodyEn: 'The {{reportTitle}} report for {{period}} from {{appLabel}} is attached. You receive it as a member of {{listName}}.',
        subjectAr: '{{reportTitle}}: {{period}}',
        bodyAr: 'مرفق تقرير {{reportTitle}} للفترة {{period}} من {{appLabel}}. تستلمه بصفتك عضوًا في {{listName}}.',
    },
    {
        key: 'distribution.memo',
        app: 'audit',
        label: 'Annual audit programme approved',
        description: 'Sent to the approval-memo distribution list when the annual programme is approved, with the memo attached.',
        critical: false,
        mergeFields: ['title', 'year', 'listName'],
        subjectEn: 'Approved: {{title}}',
        bodyEn: 'The {{year}} audit programme, {{title}}, has been approved. The approval memo is attached.',
        subjectAr: 'اعتُمد: {{title}}',
        bodyAr: 'اعتُمد برنامج التدقيق لعام {{year}}، {{title}}. مذكرة الاعتماد مرفقة.',
    },
    {
        key: 'distribution.failed',
        app: null,
        label: 'Scheduled report could not run',
        description: 'Sent to administrators when a distribution list cannot run, with the reason.',
        critical: true,
        mergeFields: ['listName', 'reason'],
        subjectEn: 'Not sent: {{listName}}',
        bodyEn: 'The scheduled report for {{listName}} could not run: {{reason}}',
        subjectAr: 'لم يُرسل: {{listName}}',
        bodyAr: 'تعذّر تشغيل التقرير المجدول لـ {{listName}}: {{reason}}',
    },
    {
        key: 'qaqc.monthly_report_missing',
        app: 'qaqc',
        label: 'Monthly QA/QC report missing',
        description: 'Sent when a project has not submitted its monthly report by the configured day.',
        critical: true,
        mergeFields: ['projectCode', 'projectName', 'month', 'deadlineDay'],
        subjectEn: 'Monthly QA/QC report not submitted: {{projectCode}}',
        bodyEn: 'The QA/QC report for {{projectName}} covering {{month}} was not submitted by day {{deadlineDay}} of the month.',
        subjectAr: 'لم يُقدَّم تقرير ضمان الجودة/مراقبة الجودة الشهري: {{projectCode}}',
        bodyAr: 'لم يُقدَّم تقرير ضمان الجودة/مراقبة الجودة لـ {{projectName}} عن {{month}} بحلول اليوم {{deadlineDay}} من الشهر.',
    },
];
export function findNotificationTemplate(key) {
    return NOTIFICATION_TEMPLATES.find((template) => template.key === key);
}
const PLACEHOLDER = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
/** The placeholders a piece of template text uses. */
export function templatePlaceholders(text) {
    return [...new Set([...text.matchAll(PLACEHOLDER)].map((match) => match[1]))];
}
/**
 * Fill a template in ONE pass.
 *
 * One pass matters: a merge value that itself contains `{{…}}` — a report
 * title someone typed — is inserted as text and never expanded, so what a
 * user writes cannot pull other fields into a message. A placeholder with no
 * value renders empty rather than leaking `{{name}}` to the reader.
 *
 * No HTML escaping here: in-app text is rendered by React, which escapes it.
 * Channels that build HTML escape at that point.
 */
export function renderTemplate(text, data) {
    return text.replace(PLACEHOLDER, (_whole, name) => {
        const value = data[name];
        return value === null || value === undefined ? '' : String(value);
    });
}
// ---------------------------------------------------------------------------
// Request contracts
// ---------------------------------------------------------------------------
export const notificationListQuerySchema = z.object({
    /** Keyset cursor: the id of the last notification on the previous page. */
    before: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    unread: z.enum(['true', 'false']).optional(),
});
export const notificationPreferencesUpdateSchema = z.object({
    preferences: z
        .array(z.object({ channel: z.enum(NOTIFICATION_CHANNELS), isEnabled: z.boolean() }))
        .min(1)
        .max(NOTIFICATION_CHANNELS.length),
});
const templateText = (max) => z.string().trim().min(1).max(max);
export const notificationTemplateOverrideSchema = z.object({
    channel: z.enum(NOTIFICATION_CHANNELS),
    subjectEn: templateText(200),
    bodyEn: templateText(4000),
    subjectAr: z.string().trim().max(200).nullable().optional(),
    bodyAr: z.string().trim().max(4000).nullable().optional(),
});
//# sourceMappingURL=notifications.js.map